import { useState } from 'react'
import { Link } from 'react-router-dom'
import { completeAyat, errorText, useJournal, wordCount, type AyatDetail, type CompleteResponse, type SaveStatus } from './ayat.ts'
import { JadikanNiat } from '../perjalanan/parts.tsx'
import { CtaBar } from './parts.tsx'

const dateFmt = new Intl.DateTimeFormat('id-ID', { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'Asia/Jakarta' })

function SaveState({ status, onRetry }: { status: SaveStatus; onRetry: () => void }) {
  return (
    <span className={`save-st ${status}`} aria-live="polite">
      {status === 'saving' && 'Menyimpan…'}
      {status === 'saved' && 'Tersimpan'}
      {status === 'error' && <>Gagal menyimpan. <button type="button" onClick={onRetry}>Coba lagi</button></>}
    </span>
  )
}

/** Step 4: today's application, the small deed, a private journal (autosaved), and the only "complete" button. */
export function Amalkan({ d, day, onDone }: { d: AyatDetail; day: number; onDone: (r: CompleteResponse) => void }) {
  const a = d.ayat
  const j = useJournal(day, { text: d.journal?.text ?? '', amalanDone: d.journal?.amalanDone ?? false })
  const completedAt = d.journal?.completedAt ?? null
  const [pending, setPending] = useState(false)
  const [error, setError] = useState('')

  const finish = async () => {
    setError('')
    setPending(true)
    try {
      // Save what was just typed first, so the journal is never lost behind the celebration.
      await j.flush()
      onDone(await completeAyat(day, wordCount(a.verses)))
    } catch (e) {
      setError(errorText(e))
    } finally {
      setPending(false)
    }
  }

  return (
    <>
      <p className="step-hint">Pilih satu hal kecil untuk dikerjakan hari ini.</p>

      <section className="card prose-card" aria-labelledby="terap-h">
        <h2 id="terap-h">Penerapan hari ini</h2>
        <p className="prose">{a.penerapan}</p>
      </section>

      <label className="card check-card">
        <input type="checkbox" checked={j.amalanDone} onChange={(e) => j.toggleAmalan(e.target.checked)} />
        <span><b>Amalan kecil</b><span>{a.amalan}</span></span>
      </label>
      {a.amalan && <p className="niat-row"><JadikanNiat text={a.amalan} source="ayat" sourceRef={String(day)} label="Jadikan niat untuk nanti" /></p>}

      <section className="card journal">
        <label htmlFor="jurnal" className="journal-l">Jurnal</label>
        <textarea
          id="jurnal"
          className="input textarea"
          rows={5}
          maxLength={4000}
          value={j.text}
          onChange={(e) => j.setText(e.target.value)}
          onBlur={() => { void j.flush() }}
          placeholder="Apa yang tersentuh dari ayat ini, atau apa yang ingin kamu coba?"
          aria-describedby="jurnal-help"
        />
        <div className="save-row">
          <p id="jurnal-help" className="help">Hanya kamu yang bisa membaca catatan ini.</p>
          <SaveState status={j.status} onRetry={() => { void j.retry() }} />
        </div>
      </section>

      <CtaBar>
        {completedAt ? (
          <>
            <p className="cta-note">Ayat ini sudah kamu jalani pada {dateFmt.format(new Date(completedAt))}.</p>
            <Link className="btn btn-ghost btn-block" to="/">Kembali ke Beranda</Link>
          </>
        ) : d.isCurrent ? (
          <>
            {error && <p className="error" role="alert" style={{ marginTop: 0, marginBottom: 8 }}>{error}</p>}
            <button type="button" className="btn btn-primary btn-block" onClick={finish} disabled={pending}>
              {pending ? <><span className="spinner" aria-hidden="true" /> Menyalakan lampu…</> : 'Selesai, terangi setapak hari ini'}
            </button>
          </>
        ) : (
          <Link className="btn btn-ghost btn-block" to="/">Kembali ke Beranda</Link>
        )}
      </CtaBar>
    </>
  )
}
